import { useContext, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import Icon from '../forms/Icon';
import { getPortalTarget } from '../utility/functions';
import { ContentScrollContext } from '../layout/Content';

const OptionsButton = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    user-select: none;
    font-weight: bold;

    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
    opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    padding: ${({ open }) => (open ? '0.2em 0.3em 2em 0.3em' : '0.2em 0.3em')};

    border-radius: ${({ open }) => (open ? '0.4rem 0.4rem 0 0' : '0.4rem')};
    border: 1px solid ${({ theme }) => `${theme.gray}40`};
    border-bottom: ${({ open, theme }) => (open ? 'none' : `1px solid ${theme.gray}40`)};

    box-sizing: border-box;
    background: ${({ theme }) => theme.body};

    transition:
        padding 0.10s ease-out,
        border-radius 0.10s ease-out,
        background-color 0.10s ease-out,
        opacity 0.10s ease-out;

    width: 230px;
`;

const Chevron = styled(Icon)`
    position: absolute;
    top: 0.3em;
    right: 0.3em;
    color: ${({ theme }) => theme.text};
    transform: rotate(${({ open }) => (open ? 0 : -90)}deg);
    transition: all 0.2s;
    pointer-events: none;
`;

const OptionsContentWrapper = styled.div`
    font-size: 0.90em;
    position: absolute;
    background: ${({ theme }) => theme.body};

    border: 1px solid ${({ theme }) => `${theme.gray}40`};
    border-top: none;
    border-radius: 0 0 0.4rem 0.4rem;

    box-sizing: border-box;
    width: 230px;
    white-space: nowrap;

    pointer-events: ${({ open }) => (open ? 'auto' : 'none')};
    visibility: ${({ open }) => (open ? 'visible' : 'hidden')};

    z-index: 1000;
`;

const OptionsContentAnimator = styled.div`
    display: grid;
    grid-template-rows: ${({ open }) => (open ? '1fr' : '0fr')};

    opacity: ${({ open }) => (open ? 1 : 0)};
    transform: ${({ open }) => (open ? 'scaleY(1)' : 'scaleY(0.96)')};
    transform-origin: top;

    transition:
        grid-template-rows 0.12s ease-out,
        opacity 0.12s ease-out,
        transform 0.12s ease-out;
`;

const OptionsContent = styled.div`
    overflow: hidden;

    display: flex;
    flex-direction: column;
    row-gap: 0.5rem;
    padding: 0.3rem;

    & > * {
        width: 100%;
        white-space: nowrap;
    }
`;

// Offscreen container to measure OptionsContent
const MeasureShell = styled.div`
    position: fixed;
    top: -10000px;
    left: -10000px;
    visibility: hidden;
    pointer-events: none;
    z-index: -1;

    font-size: 0.90em;
    width: 230px;
    white-space: nowrap;
`;

const TableOptionsMenu = ({
    tableId,
    loading,
    anchorRef,
    isFullscreen,
    onNeedsSpace,
    children
}) => {
    const [open, setOpen] = useState(false);
    const [rect, setRect] = useState(null);

    const triggerRef = useRef(null);
    const menuSurfaceRef = useRef(null);

    const measureOptionsRef = useRef(null);
    const menuHeightRef = useRef(null);
    const spaceCalcComplete = useRef(false);

    const scrollContainerRef = useContext(ContentScrollContext);

    const portalTarget = getPortalTarget({
        contextEl: scrollContainerRef?.current
    });

    // Measure actual menu options height from offscreen, non-animated copy
    useLayoutEffect(() => {
        if (!measureOptionsRef.current) return;

        const r = measureOptionsRef.current.getBoundingClientRect();
        if (r.height > 0) {
            menuHeightRef.current = r.height;
        }
    }, [children]);

    // To avoid scroll conflicts, detect menu overflow 
    // and tell parent table to add bottom margin if necessary
    // typically occurs when empty table is last child in a tab group
    useLayoutEffect(() => {
        if (
            !triggerRef.current ||
            !menuHeightRef.current ||
            spaceCalcComplete.current ||
            !scrollContainerRef?.current
        ) {
            return;
        }

        const BUFFER = 8; // arbitrary tolerance to avoid fine pixel issues on eval
        const container = scrollContainerRef.current;
        const containerRect = container.getBoundingClientRect();
        const triggerRect = triggerRef.current.getBoundingClientRect();

        const triggerOffsetTop =
            triggerRect.top - containerRect.top + container.scrollTop;

        const projectedMenuBottom =
            triggerOffsetTop +
            triggerRect.height +
            menuHeightRef.current +
            BUFFER;

        if (projectedMenuBottom > container.scrollHeight) {         
            onNeedsSpace?.();
        }
        spaceCalcComplete.current = true;
    }, [menuHeightRef.current]);

    // Auto-close if table re-enters loading
    useLayoutEffect(() => {
        if (loading && open) {
            setOpen(false);
        }
    }, [loading, open]);

    //position menu
    useLayoutEffect(() => {
        if (!open || !triggerRef.current || !portalTarget) return;

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const scrollEl = scrollContainerRef?.current;

        const isPortalingIntoScrollContainer =
            !!scrollEl && portalTarget === scrollEl;

        if (isPortalingIntoScrollContainer) {
            const containerRect = scrollEl.getBoundingClientRect();

            setRect({
                top: triggerRect.bottom - containerRect.top + scrollEl.scrollTop,
                left: triggerRect.left - containerRect.left + scrollEl.scrollLeft
            });
            return;
        }

        const portalRect = portalTarget.getBoundingClientRect();

        setRect({
            top: triggerRect.bottom - portalRect.top + (portalTarget.scrollTop ?? 0),
            left: triggerRect.left - portalRect.left + (portalTarget.scrollLeft ?? 0)
        });
    }, [open, portalTarget, scrollContainerRef, isFullscreen]);

    // Close on click outside
    useLayoutEffect(() => {
        if (!open) return;

        const onPointerDown = e => {
            if (
                triggerRef.current?.contains(e.target) ||
                menuSurfaceRef.current?.contains(e.target)
            ) {
                return;
            }

            setOpen(false);
            triggerRef.current?.focus();
        };

        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [open]);

    // Close on scroll
    useLayoutEffect(() => {
        if (!open) return;

        const onScroll = () => {
            setOpen(false);
            triggerRef.current?.focus();
        };

        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [open]);

    // Close on escape
    useLayoutEffect(() => {
        if (!open) return;

        const onKeyDown = e => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                setOpen(false);
                triggerRef.current?.focus();
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open]);

    // Close on fullscreen exit
    useLayoutEffect(() => {
        if (!open) return;

        const onFullscreenChange = () => {
            if (!document.fullscreenElement) {
                setOpen(false);
                triggerRef.current?.focus();
            }
        };

        document.addEventListener('fullscreenchange', onFullscreenChange);
        return () =>
            document.removeEventListener('fullscreenchange', onFullscreenChange);
    }, [open]);

    if (!anchorRef?.current || !portalTarget) return null;

    return (
        <>
            <MeasureShell>
                <OptionsContent ref={measureOptionsRef}>
                    {children}
                </OptionsContent>
            </MeasureShell>

            <OptionsButton
                ref={triggerRef}
                open={open}
                disabled={loading}
                onClick={() => {
                    if (loading) return;
                    setOpen(v => !v);
                }}
            >
                <label style={{ width: '100%' }}>Options</label>
                <Chevron icon="chevronDown" open={open} />
            </OptionsButton>

            {createPortal(
                <OptionsContentWrapper
                    ref={menuSurfaceRef}
                    open={open}
                    style={{
                        top: rect?.top,
                        left: rect?.left
                    }}
                >
                    <OptionsContentAnimator open={open}>
                        <OptionsContent
                            onClick={() => {
                                setOpen(false);
                                triggerRef.current?.focus();
                            }}
                        >
                            {children}
                        </OptionsContent>
                    </OptionsContentAnimator>
                </OptionsContentWrapper>,
                portalTarget
            )}
        </>
    );
};

export default TableOptionsMenu;
